import React, { useState } from 'react';
import { Copy, Download, RotateCcw, Code } from 'lucide-react';

interface HTMLFormatterProps {
  initialValue?: string;
  className?: string;
}

const HTMLFormatter: React.FC<HTMLFormatterProps> = ({ 
  initialValue = '', 
  className = '' 
}) => {
  const [inputHtml, setInputHtml] = useState<string>(initialValue);
  const [formattedHtml, setFormattedHtml] = useState<string>('');
  const [indentSize, setIndentSize] = useState<number>(2);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const formatHTML = (html: string, indent: number = 2): string => {
    if (!html.trim()) return '';

    const tab = ' '.repeat(indent);
    let result = '';
    let indentLevel = 0;
    let inTag = false;
    let inComment = false;
    let inScript = false;
    let inStyle = false;

    // Remove extra whitespace and normalize
    html = html.replace(/>\s+</g, '><').trim();

    const lines = html.split(/(?=<)|(?<=>)/);
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trim();
      if (!line) continue;

      // Handle comments
      if (line.includes('<!--')) {
        inComment = true;
        result += tab.repeat(indentLevel) + line + '\n';
        if (line.includes('-->')) {
          inComment = false;
        }
        continue;
      }
      
      if (inComment) {
        result += tab.repeat(indentLevel) + line + '\n';
        if (line.includes('-->')) {
          inComment = false;
        }
        continue;
      }

      // Handle script and style tags
      if (line.toLowerCase().includes('<script')) {
        inScript = true;
        result += tab.repeat(indentLevel) + line + '\n';
        indentLevel++;
        continue;
      }
      
      if (line.toLowerCase().includes('</script>')) {
        indentLevel--;
        inScript = false;
        result += tab.repeat(indentLevel) + line + '\n';
        continue;
      }
      
      if (line.toLowerCase().includes('<style')) {
        inStyle = true;
        result += tab.repeat(indentLevel) + line + '\n';
        indentLevel++;
        continue;
      }
      
      if (line.toLowerCase().includes('</style>')) {
        indentLevel--;
        inStyle = false;
        result += tab.repeat(indentLevel) + line + '\n';
        continue;
      }

      if (inScript || inStyle) {
        result += tab.repeat(indentLevel) + line + '\n';
        continue;
      }

      // Handle regular tags
      if (line.startsWith('</')) {
        // Closing tag
        indentLevel = Math.max(0, indentLevel - 1);
        result += tab.repeat(indentLevel) + line + '\n';
      } else if (line.startsWith('<') && !line.includes('/>')) {
        // Opening tag (not self-closing)
        result += tab.repeat(indentLevel) + line + '\n';
        
        // Check if it's not a void element
        const tagName = line.match(/<(\w+)/)?.[1]?.toLowerCase();
        const voidElements = ['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr'];
        
        if (tagName && !voidElements.includes(tagName)) {
          indentLevel++;
        }
      } else {
        // Self-closing tag or text content
        result += tab.repeat(indentLevel) + line + '\n';
      }
    }

    return result.trim();
  };

  const handleFormat = (): void => {
    try {
      const formatted = formatHTML(inputHtml, indentSize);
      setFormattedHtml(formatted);
    } catch (error) {
      setFormattedHtml('Error formatting HTML: ' + (error as Error).message);
    }
  };

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(formattedHtml);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDownload = (): void => {
    const blob = new Blob([formattedHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleReset = (): void => {
    setInputHtml('');
    setFormattedHtml('');
  };

  return (
    <div className={`max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg ${className}`}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center gap-2">
          <Code className="w-6 h-6" />
          HTML Formatter
        </h2>
        <p className="text-gray-600">Format and prettify your HTML code with proper indentation</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Input HTML
            </label>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Indent Size:</label>
              <select
                value={indentSize}
                onChange={(e) => setIndentSize(Number(e.target.value))}
                className="text-xs border border-gray-300 rounded px-2 py-1"
              >
                <option value={2}>2 spaces</option>
                <option value={4}>4 spaces</option>
                <option value={8}>8 spaces</option>
              </select>
            </div>
          </div>
          
          <textarea
            value={inputHtml}
            onChange={(e) => setInputHtml(e.target.value)}
            placeholder="Paste your HTML code here..."
            className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          
          <div className="flex gap-2">
            <button
              onClick={handleFormat}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Code className="w-4 h-4" />
              Format HTML
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>

        {/* Output Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Formatted HTML
            </label>
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                disabled={!formattedHtml}
                className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Copy className="w-3 h-3" />
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handleDownload}
                disabled={!formattedHtml}
                className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-3 h-3" />
                Download
              </button>
            </div>
          </div>
          
          <div className="relative">
            <pre className="w-full h-64 p-3 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm overflow-auto">
              <code>{formattedHtml || 'Formatted HTML will appear here...'}</code>
            </pre>
          </div>
          
          {formattedHtml && (
            <div className="text-xs text-gray-500">
              Lines: {formattedHtml.split('\n').length} | 
              Characters: {formattedHtml.length}
            </div>
          )}
        </div>
      </div>

      {/* Sample HTML for testing */}
      <div className="mt-6 p-4 bg-gray-50 rounded-md">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Sample HTML to test:</h3>
        <code className="text-xs text-gray-600 block">
          {`<div><h1>Hello World</h1><p>This is a <strong>sample</strong> HTML.</p><ul><li>Item 1</li><li>Item 2</li></ul></div>`}
        </code>
      </div>
    </div>
  );
};

export default HTMLFormatter;